import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BookOpen, CheckCircle2, Circle, ExternalLink, GraduationCap, Loader2, Plus, Sparkles, Trash2, X, Video, FileText, Layers } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { useLearningResources } from '../../hooks/useLearningResources'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { supabase } from '../../lib/supabaseClient'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// Owner-gated page that collects learning resources (articles, video
// lessons, courses) per topic. Topics are free-form; the user can
// have any number — sport, language, programming, etc.
//
// Surfaces: topic chip row, type tab row, resource cards. Add button
// drops a modal that auto-fills via fetch-url-meta when a URL is
// pasted. AI Suggest button calls learning-suggest for the active
// topic.

const TYPES = ['all', 'article', 'video', 'lesson', 'course', 'other']

const TYPE_ICONS = {
  article: FileText,
  video: Video,
  lesson: GraduationCap,
  course: Layers,
  other: BookOpen,
}

export function LearningPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { user, loading: authLoading } = useAuth()
  const isAr = i18n.language === 'ar'

  // Owner gate. The route is mounted globally but only the owner can
  // see content; everyone else gets a quiet redirect to /phoenix.
  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const activeTopic = params.get('topic') || ''
  const activeType = params.get('type') || 'all'

  const setTopic = useCallback((next) => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev)
      if (next) sp.set('topic', next)
      else sp.delete('topic')
      return sp
    }, { replace: true })
  }, [setParams])

  const setType = useCallback((next) => {
    setParams((prev) => {
      const sp = new URLSearchParams(prev)
      if (next && next !== 'all') sp.set('type', next)
      else sp.delete('type')
      return sp
    }, { replace: true })
  }, [setParams])

  const { resources, allTopics, loading, refetch, add, remove, toggleCompleted, counts } = useLearningResources({
    topic: activeTopic || undefined,
    type: activeType === 'all' ? undefined : activeType,
  })

  // First-visit bootstrap: when the owner opens the page for the
  // first time and has no resources / topics at all, auto-pull a
  // starter set of AI-curated resources for the topic they told us
  // they're learning (swimming, for now — eventually a per-user
  // "interests" field would drive this). Persists a flag so a user
  // who manually clears everything doesn't get the same starter
  // bootstrap re-injected on next open.
  const BOOTSTRAP_FLAG = 'phoenix:learning-bootstrapped'
  const [bootstrapping, setBootstrapping] = useState(false)
  useEffect(() => {
    if (loading) return
    if (resources.length > 0 || allTopics.length > 0) return
    let bootstrapped = false
    try { bootstrapped = localStorage.getItem(BOOTSTRAP_FLAG) === 'true' } catch { /* private mode */ }
    if (bootstrapped) return
    setBootstrapping(true)
    const topic = isAr ? 'سباحة' : 'Swimming'
    supabase.functions
      .invoke('learning-suggest', { body: { topic, language: isAr ? 'ar' : 'en', count: 8 } })
      .then(async ({ data, error: err }) => {
        if (err || !Array.isArray(data?.resources)) return
        for (const r of data.resources) {
          await add({
            url: r.url,
            title: r.title,
            type: r.type || 'other',
            topic,
            source_host: r.source || null,
            notes: r.description || null,
          })
        }
        try { localStorage.setItem(BOOTSTRAP_FLAG, 'true') } catch { /* */ }
        if (!activeTopic) setTopic(topic)
      })
      .finally(() => setBootstrapping(false))
  }, [loading, resources.length, allTopics.length, isAr, add, activeTopic, setTopic])

  // Topic list combines the user's existing topics + the active one
  // (so a freshly-typed topic from the add modal shows up even before
  // a refetch lands).
  const topicChips = useMemo(() => {
    const set = new Set(allTopics)
    if (activeTopic) set.add(activeTopic)
    return Array.from(set).sort()
  }, [allTopics, activeTopic])

  const [addOpen, setAddOpen] = useState(false)
  const [suggestOpen, setSuggestOpen] = useState(false)

  const handleRefresh = useCallback(() => performRefresh([refetch]), [refetch])
  const goBack = () => navigate('/')

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
              <BookOpen className="w-5 h-5 text-app-primary-500" />
              {t('learning.title')}
            </h1>
            <p className="text-[11px] text-muted-foreground mt-0.5" dir="auto">
              {t('learning.subtitle', { count: counts.total, done: counts.completed })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            aria-label={t('learning.addResource')}
            className="p-2 rounded-md bg-app-primary-500 text-white hover:bg-app-primary-500/90"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>

        {/* Topic chips */}
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          <Chip active={!activeTopic} onClick={() => setTopic(null)}>
            {t('learning.allTopics')}
          </Chip>
          {topicChips.map((tp) => (
            <Chip key={tp} active={activeTopic === tp} onClick={() => setTopic(tp)}>
              {tp}
            </Chip>
          ))}
        </div>

        {/* Type tabs */}
        <div className="flex gap-1 p-1 bg-muted/60 rounded-full overflow-x-auto">
          {TYPES.map((tp) => (
            <button
              key={tp}
              type="button"
              onClick={() => setType(tp)}
              className={cn(
                'flex-1 h-9 rounded-full text-xs font-semibold transition-colors whitespace-nowrap px-3',
                activeType === tp
                  ? 'bg-app-primary-500 text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              dir="auto"
            >
              {t(`learning.type.${tp}`)}
            </button>
          ))}
        </div>

        {/* AI suggest button — only when a topic is active. The
            empty state surfaces its own version of this CTA when
            the user has nothing yet. */}
        {activeTopic && (
          <button
            type="button"
            onClick={() => setSuggestOpen(true)}
            className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-app-primary-500/40 text-app-primary-500 text-xs font-semibold hover:bg-app-primary-500/5"
            dir="auto"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t('learning.suggestForTopic', { topic: activeTopic })}
          </button>
        )}

        {loading || bootstrapping ? (
          <div className="space-y-2">
            {bootstrapping && (
              <p className="text-xs text-app-primary-500 inline-flex items-center gap-1.5" dir="auto">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t('learning.bootstrapping')}
              </p>
            )}
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : resources.length === 0 ? (
          <EmptyState
            icon={BookOpen}
            title={t('learning.empty')}
            description={t('learning.emptyHint')}
            action={
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-app-primary-500 text-white text-xs font-semibold"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('learning.addResource')}
              </button>
            }
          />
        ) : (
          <div className="space-y-2">
            {resources.map((r) => (
              <ResourceCard
                key={r.id}
                resource={r}
                onToggle={() => toggleCompleted(r.id)}
                onRemove={() => remove(r.id)}
                isAr={isAr}
                t={t}
              />
            ))}
          </div>
        )}

        {addOpen && (
          <AddResourceModal
            initialTopic={activeTopic}
            onClose={() => setAddOpen(false)}
            onSubmit={async (input) => {
              const saved = await add(input)
              if (saved && saved.topic) setTopic(saved.topic)
              return saved
            }}
            t={t}
            isAr={isAr}
          />
        )}

        {suggestOpen && activeTopic && (
          <SuggestModal
            topic={activeTopic}
            language={isAr ? 'ar' : 'en'}
            onClose={() => setSuggestOpen(false)}
            onAdd={(r) => add({ ...r, topic: activeTopic, source_host: r.source || null })}
            t={t}
            isAr={isAr}
          />
        )}
      </div>
    </PullToRefresh>
  )
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-shrink-0 h-9 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
        active
          ? 'bg-app-primary-500 text-white'
          : 'bg-muted text-muted-foreground hover:bg-accent',
      )}
      dir="auto"
    >
      {children}
    </button>
  )
}

function ResourceCard({ resource, onToggle, onRemove, isAr, t }) {
  const Icon = TYPE_ICONS[resource.type] || BookOpen
  return (
    <Card className={cn(
      'hover:border-app-primary-500/40 transition-colors overflow-hidden',
      resource.completed && 'opacity-60',
    )}>
      <CardContent className="p-3 flex gap-3">
        <button
          type="button"
          onClick={onToggle}
          aria-label={t(resource.completed ? 'learning.markIncomplete' : 'learning.markComplete')}
          className="flex-shrink-0 mt-0.5"
        >
          {resource.completed ? (
            <CheckCircle2 className="w-5 h-5 text-app-primary-500 fill-current" />
          ) : (
            <Circle className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
        {resource.image_url && (
          <img
            src={resource.image_url.replace(/&amp;/g, '&')}
            alt=""
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
            className="w-16 h-16 rounded-md object-cover flex-shrink-0 bg-muted"
          />
        )}
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm leading-snug', resource.completed ? 'font-normal line-through' : 'font-semibold')} dir="auto">
            {resource.title}
          </p>
          <div className="flex items-center gap-1.5 mt-1 text-[11px] text-muted-foreground flex-wrap" dir="auto">
            <Icon className="w-3 h-3" />
            <span>{t(`learning.type.${resource.type}`)}</span>
            {resource.source_host && (
              <>
                <span>·</span>
                <span dir="ltr">{resource.source_host}</span>
              </>
            )}
          </div>
          {resource.notes && (
            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2" dir="auto">
              {resource.notes}
            </p>
          )}
          <div className="flex items-center gap-2 mt-2">
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-app-primary-500 hover:underline"
            >
              <ExternalLink className="w-3 h-3 rtl:-scale-x-100" />
              {t('learning.open')}
            </a>
            <button
              type="button"
              onClick={onRemove}
              aria-label={t('common.delete')}
              className="ms-auto text-muted-foreground hover:text-red-500 inline-flex items-center gap-1 text-[11px]"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function AddResourceModal({ initialTopic, onClose, onSubmit, t, isAr }) {
  const [url, setUrl] = useState('')
  const [title, setTitle] = useState('')
  const [type, setType] = useState('article')
  const [topic, setTopic] = useState(initialTopic || '')
  const [notes, setNotes] = useState('')
  const [imageUrl, setImageUrl] = useState(null)
  const [sourceHost, setSourceHost] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Debounced auto-fetch when the user pastes / finishes typing a URL.
  useEffect(() => {
    const u = url.trim()
    if (!/^https?:\/\//i.test(u)) return undefined
    const timer = setTimeout(async () => {
      setFetching(true)
      setError(null)
      try {
        const { data, error: err } = await supabase.functions.invoke('fetch-url-meta', { body: { url: u } })
        if (err) throw err
        if (data?.title) setTitle((t) => t || data.title)
        if (data?.image_url) setImageUrl(data.image_url)
        if (data?.source_host) setSourceHost(data.source_host)
        if (data?.inferred_type) setType((t) => t || data.inferred_type)
      } catch (e) {
        // Silent failure — user can fill manually.
      } finally {
        setFetching(false)
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [url])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving) return
    setSaving(true)
    setError(null)
    try {
      const saved = await onSubmit({
        url: url.trim(),
        title: title.trim() || url.trim(),
        type,
        topic: topic.trim(),
        notes: notes.trim() || null,
        image_url: imageUrl,
        source_host: sourceHost,
      })
      if (saved) onClose()
      else setError(t('learning.saveFailed'))
    } catch (e) {
      setError(e?.message || 'failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto"
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className="flex items-center gap-2">
          <Plus className="w-5 h-5 text-app-primary-500" />
          <h2 className="text-base font-bold text-foreground flex-1">{t('learning.addResource')}</h2>
          <button type="button" onClick={onClose} aria-label={t('common.cancel')} className="p-1 rounded-md text-muted-foreground hover:bg-accent">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div>
          <label className="block text-xs text-muted-foreground mb-1">{t('learning.fields.url')}</label>
          <input
            type="url"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..."
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
          />
          {fetching && (
            <p className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              {t('learning.fetchingMeta')}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs text-muted-foreground mb-1">{t('learning.fields.title')}</label>
          <input
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('learning.fields.topic')}</label>
            <input
              type="text"
              required
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t('learning.fields.topicPlaceholder')}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('learning.fields.type')}</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-10 w-full px-3 rounded-md border border-input bg-background text-foreground text-sm"
            >
              {TYPES.filter((tp) => tp !== 'all').map((tp) => (
                <option key={tp} value={tp}>{t(`learning.type.${tp}`)}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs text-muted-foreground mb-1">{t('learning.fields.notes')}</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('learning.fields.notesPlaceholder')}
            className="w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm resize-none"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving || !url.trim() || !title.trim() || !topic.trim()}
            className="flex-1 h-10 rounded-md bg-app-primary-500 text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t('common.save')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 h-10 rounded-md border border-input text-sm"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}

function SuggestModal({ topic, language, onClose, onAdd, t, isAr }) {
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(new Set())

  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    supabase.functions.invoke('learning-suggest', { body: { topic, language } })
      .then(({ data, error: err }) => {
        if (!alive) return
        if (err) { setError(err.message); setItems([]) }
        else if (data?.error) { setError(data.error); setItems([]) }
        else setItems(Array.isArray(data?.resources) ? data.resources : [])
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [topic, language])

  const handleAdd = async (idx) => {
    setAdding((prev) => new Set(prev).add(idx))
    const r = items[idx]
    await onAdd({
      url: r.url,
      title: r.title,
      type: r.type || 'other',
      source_host: r.source || null,
      notes: r.description || null,
    })
    setAdding((prev) => {
      const next = new Set(prev)
      next.delete(idx)
      return next
    })
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, _added: true } : it)))
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-card border border-border rounded-t-2xl sm:rounded-2xl p-4 max-h-[85vh] flex flex-col"
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-app-primary-500" />
          <h2 className="text-base font-bold text-foreground flex-1">{t('learning.suggestTitle', { topic })}</h2>
          <button type="button" onClick={onClose} aria-label={t('common.cancel')} className="p-1 rounded-md text-muted-foreground hover:bg-accent">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2">
          {loading && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {t('learning.suggestLoading')}
            </div>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          {!loading && !error && items.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('learning.suggestEmpty')}</p>
          )}
          {items.map((r, idx) => (
            <div key={idx} className="border border-border rounded-md p-3">
              <p className="text-sm font-semibold text-foreground" dir="auto">{r.title}</p>
              {r.description && (
                <p className="text-xs text-muted-foreground mt-1" dir="auto">{r.description}</p>
              )}
              <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground">
                <span>{t(`learning.type.${r.type || 'other'}`)}</span>
                {r.source && (<><span>·</span><span dir="ltr">{r.source}</span></>)}
              </div>
              <div className="flex gap-2 mt-2">
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-app-primary-500 hover:underline inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3 rtl:-scale-x-100" />
                  {t('learning.open')}
                </a>
                <button
                  type="button"
                  disabled={adding.has(idx) || r._added}
                  onClick={() => handleAdd(idx)}
                  className={cn(
                    'ms-auto text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded',
                    r._added
                      ? 'text-app-primary-500 bg-app-primary-500/10'
                      : 'text-white bg-app-primary-500 hover:bg-app-primary-500/90 disabled:opacity-60',
                  )}
                >
                  {r._added ? <CheckCircle2 className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {r._added ? t('learning.added') : t('learning.addResource')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default LearningPage
